(() => {
  const selectors = {
    root: "[data-aw-team-uniform-form]",
    trigger: "[data-aw-team-uniform-form-modal-trigger]",
    modal: "[data-aw-team-uniform-form-modal]",
    close: "[data-aw-team-uniform-form-modal-close]",
    sameChoice: "[data-aw-team-uniform-form-billing-same]",
    differentChoice: "[data-aw-team-uniform-form-billing-different]",
    billingFields: "[data-aw-team-uniform-form-billing-fields]",
    fileInput: ".aw-team-uniform-form__file-field input[type='file']"
  };

  const instances = new Map();

  class AWTeamUniformForm {
    constructor(root) {
      this.root = root;
      this.trigger = root.querySelector(selectors.trigger);
      this.modal = root.querySelector(selectors.modal);
      this.closes = Array.from(root.querySelectorAll(selectors.close));
      this.sameChoice = root.querySelector(selectors.sameChoice);
      this.differentChoice = root.querySelector(selectors.differentChoice);
      this.billingFields = root.querySelector(selectors.billingFields);
      this.fileInputs = Array.from(root.querySelectorAll(selectors.fileInput));

      this.onKeydown = this.handleKeydown.bind(this);
      this.onTriggerClick = this.open.bind(this);
      this.onCloseClick = this.close.bind(this);
      this.onSameChange = this.handleSameChange.bind(this);
      this.onDifferentChange = this.handleDifferentChange.bind(this);
      this.onFileInputChange = this.handleFileInputChange.bind(this);

      this.bind();
      this.setBillingMode(this.differentChoice && this.differentChoice.checked ? "different" : "same");
      this.syncFileNames();
    }

    bind() {
      if (this.trigger) {
        this.trigger.addEventListener("click", this.onTriggerClick);
      }

      this.closes.forEach((button) => {
        button.addEventListener("click", this.onCloseClick);
      });

      if (this.sameChoice) {
        this.sameChoice.addEventListener("change", this.onSameChange);
      }

      if (this.differentChoice) {
        this.differentChoice.addEventListener("change", this.onDifferentChange);
      }

      this.fileInputs.forEach((input) => {
        input.addEventListener("change", this.onFileInputChange);
      });
    }

    handleSameChange() {
      this.setBillingMode("same");
    }

    handleDifferentChange() {
      this.setBillingMode("different");
    }

    setBillingMode(mode) {
      if (!this.sameChoice || !this.differentChoice) {
        return;
      }

      const isDifferent = mode === "different";
      this.sameChoice.checked = !isDifferent;
      this.differentChoice.checked = isDifferent;

      if (this.billingFields) {
        this.billingFields.hidden = !isDifferent;
      }
    }

    handleFileInputChange(event) {
      this.syncFileName(event.currentTarget);
    }

    syncFileName(input) {
      const fileField = input.closest(".aw-team-uniform-form__file-field");
      const fileNameNode = fileField ? fileField.querySelector("[data-aw-team-uniform-form-file-name]") : null;
      const hasFile = Boolean(input.files && input.files.length);

      if (!fileNameNode) {
        return;
      }

      fileNameNode.textContent = hasFile ? input.files[0].name : "";

      if (fileField) {
        fileField.classList.toggle("is-selected", hasFile);
      }
    }

    syncFileNames() {
      this.fileInputs.forEach((input) => {
        this.syncFileName(input);
      });
    }

    open() {
      if (!this.modal) {
        return;
      }

      this.modal.hidden = false;
      this.modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("is-aw-team-uniform-form-modal-open");
      document.addEventListener("keydown", this.onKeydown);
    }

    close() {
      if (!this.modal) {
        return;
      }

      this.modal.hidden = true;
      this.modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("is-aw-team-uniform-form-modal-open");
      document.removeEventListener("keydown", this.onKeydown);

      if (this.trigger) {
        this.trigger.focus();
      }
    }

    handleKeydown(event) {
      if (event.key === "Escape") {
        this.close();
      }
    }

    destroy() {
      if (this.trigger) {
        this.trigger.removeEventListener("click", this.onTriggerClick);
      }

      this.closes.forEach((button) => {
        button.removeEventListener("click", this.onCloseClick);
      });

      if (this.sameChoice) {
        this.sameChoice.removeEventListener("change", this.onSameChange);
      }

      if (this.differentChoice) {
        this.differentChoice.removeEventListener("change", this.onDifferentChange);
      }

      this.fileInputs.forEach((input) => {
        input.removeEventListener("change", this.onFileInputChange);
      });

      document.removeEventListener("keydown", this.onKeydown);
      document.body.classList.remove("is-aw-team-uniform-form-modal-open");
    }
  }

  function init(root) {
    const sectionId = root.closest("[id]")?.id || root.dataset.sectionId || `aw-team-uniform-form-${instances.size}`;

    if (instances.has(sectionId)) {
      instances.get(sectionId).destroy();
    }

    instances.set(sectionId, new AWTeamUniformForm(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach(init);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector(selectors.root);

    if (!root) {
      return;
    }

    const sectionId = root.closest("[id]")?.id || root.dataset.sectionId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (instance) {
      instance.destroy();
      instances.delete(sectionId);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(document));
  } else {
    initAll(document);
  }
})();
